import { getTokenFromCookie } from "@/lib/cookie.ts";
import axios from "axios";
import { API_URL, TENANT_SLUG } from "@/config";
import printFile from "@/utils/printFile";
import store from "@/lib/store";
import { createTrackedEventSource } from "@/lib/eventSourceRegistry";

type MercureSubscription = {
  url: string;
  topic: string;
  token?: string;
};

type MercureResponseHeaders = Record<string, string> & {
  Link?: string[];
};

type MercureApiResponse = {
  headers?: MercureResponseHeaders;
  data: {
    mercureInfo: {
      mercureTopic: string;
      mercureToken: string;
    };
  };
};

type ExtractedLink = {
  url: string;
  rel: string;
};

type MercureFileMessage = {
  filename: string;
  dispatchId: string;
  status: "done" | "begin" | "error";
  mercureInfo: {
    mercureTopic: string;
    mercureToken: string;
  };
};

export function extractLinkHeader(linkHeader: string): ExtractedLink | undefined {
  return linkHeader
    .split(",")
    .map((link) => {
      const [url, rel] = link.split(";");
      return {
        url: url.trim().replace(/[<>]/g, ""),
        rel: rel.trim().replace(/rel="|"/g, ""),
      };
    })
    .find((link) => link.rel === "mercure");
}

export function useMercureSubscription() {
  function subscribeFromResponse(
    response: MercureApiResponse,
    print = false,
  ): Promise<MercureFileMessage> {
    if (!response.headers) {
      return Promise.reject("Response headers not found in response");
    }
    const linkHeader = response.headers.link;

    if (!linkHeader) {
      return Promise.reject("No Link header found in response header");
    }
    // extract url from Link header
    const mercureLink = extractLinkHeader(linkHeader);

    if (!mercureLink) {
      return Promise.reject("No mercure Link header found in response header");
    }

    const promise = subscribe(
      {
        url: mercureLink.url,
        topic: response.data.mercureInfo.mercureTopic,
        token: response.data.mercureInfo.mercureToken,
      },
      print,
    );

    promise.then((message) => {
      store.dispatch("reports/ackReport", message.dispatchId);
      return message;
    });

    return promise;
  }

  function subscribe(
    subscription: MercureSubscription,
    print = false,
  ): Promise<MercureFileMessage> {
    const url = new URL(subscription.url);
    url.searchParams.append("topic", subscription.topic);
    if (subscription.token) {
      url.searchParams.append("authorization", subscription.token);
    }

    return new Promise((resolve, reject) => {
      const eventSource = createTrackedEventSource(url);
      eventSource.onerror = (error) => {
        console.error("Mercure connection error", error);
        eventSource.close();
        reject(error);
      };

      eventSource.onmessage = async (event) => {
        const message = JSON.parse(event.data) as MercureFileMessage;
        if (message.status === "done" && message.filename) {
          if (print) {
            await printDownloadedFile(message.filename);
          } else {
            await downloadFile(message.filename);
          }
          eventSource.close();
          return resolve(message);
        }

        if (message.status === "error") {
          eventSource.close();
          return reject("Error generating report: ");
        }
      };
    });
  }

  async function printDownloadedFile(filename: string) {
    try {
      const token = getTokenFromCookie();

      const response = await axios({
        method: "GET",
        url: `${API_URL}/reports/downloadReport/${filename}`,
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-Slug": TENANT_SLUG,
          "X-Fe-Version": import.meta.env.VITE_APP_VERSION ?? "unknown",
        },
      });

      printFile(response);
    } catch (error) {
      console.error("Download failed:", error);
    }
  }
  async function downloadFile(filename: string) {
    try {
      const token = getTokenFromCookie();

      const response = await axios({
        method: "GET",
        url: `${API_URL}/reports/downloadReport/${filename}`,
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-Slug": TENANT_SLUG,
          "X-Fe-Version": import.meta.env.VITE_APP_VERSION ?? "unknown",
        },
      });

      // Create blob URL and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  }

  return {
    subscribe,
    subscribeFromResponse,
  };
}
